import { addDays, dateOf, fmtDate, timeOf } from './dates';

/**
 * WhatsApp inbox (prototype lines 1613-1630) on real `wa_conversations` / `wa_messages` rows: list filters,
 * unread totals and labels, message time labels and the Meta 24-hour customer-service window.
 */

export const WA_FILTERS = ['all', 'unread'] as const;
export type WaFilter = (typeof WA_FILTERS)[number];

export const WA_DIRECTIONS = ['in', 'out'] as const;
export type WaDirection = (typeof WA_DIRECTIONS)[number];

export const WA_MESSAGE_STATUSES = ['received', 'queued', 'sent', 'delivered', 'read', 'failed'] as const;
export type WaMessageStatus = (typeof WA_MESSAGE_STATUSES)[number];

/** Meta's customer-service window: free-form text only within 24 h of the last inbound message. */
export const WA_WINDOW_HOURS = 24;
export const WA_WINDOW_MS = WA_WINDOW_HOURS * 60 * 60 * 1000;

export interface WaConversationLike {
  id: string;
  name: string;
  /** +E.164 as stored (`+38349222333`). */
  phone: string;
  unread: number;
  lastMessageAt: string | null;
  lastInboundAt: string | null;
}

export interface WaMessageLike {
  id: string;
  direction: WaDirection;
  body: string;
  sentAt: string;
  status: string;
}

/** Verbatim prototype strings of the inbox (page HTML 983-993 and renderWaConversations/renderWaChat). */
export const WA_TEXT = {
  filterAll: 'Të gjitha',
  filterUnread: 'Pa lexuar',
  searchPlaceholder: 'Kërko emër ose numër...',
  unreadSuffix: 'pa lexuar',
  allRead: 'Të gjitha të lexuara',
  noConversations: 'Nuk u gjet asnjë bisedë.',
  activeConversation: 'Bisedë aktive',
  channelSuffix: '· WhatsApp',
  staffSuffix: ' · Stafi',
  replyPlaceholder: 'Shkruaj përgjigjen...',
  send: 'Dërgo',
  yesterday: 'Dje',
  unknownName: 'Numër i panjohur',
} as const;

/** Prototype `waTotalUnread()`. */
export function totalUnread(list: readonly Pick<WaConversationLike, 'unread'>[]): number {
  return list.reduce((n, c) => n + (Number(c.unread) || 0), 0);
}

/** Prototype `waSyncBadge()` label: `N pa lexuar` or `Të gjitha të lexuara`. */
export function unreadLabel(n: number): string {
  return n ? `${n} ${WA_TEXT.unreadSuffix}` : WA_TEXT.allRead;
}

/** Display name: the stored name or the prototype's `Numër i panjohur` for a blank contact. */
export function conversationName(c: Pick<WaConversationLike, 'name'>): string {
  return c.name.trim() || WA_TEXT.unknownName;
}

/** A query made only of digits, spaces, `+`, `-`, `(`, `)` is a phone search (`049 222 333`, `+38349`). */
const PHONE_QUERY_RE = /^[+\d\s().-]+$/;

/** Digits of a (partial) phone query as they appear in a stored +E.164: `00…`/leading `0` → `383…`. */
export function phoneQueryDigits(query: string): string {
  let d = query.replace(/\D/g, '');
  if (d.startsWith('00')) d = d.slice(2);
  else if (d.startsWith('0')) d = '383' + d.slice(1);
  return d;
}

/**
 * Prototype `renderWaConversations` filter: unread-only when `filter === 'unread'`; the search matches the
 * lower-cased name or the phone (raw includes, as the prototype). Because phones are stored as +E.164 while
 * customers are typed the Kosovo way (`049 222 333`), a phone-like query also matches its `383…` digits.
 */
export function filterConversations<T extends WaConversationLike>(
  list: readonly T[],
  filter: WaFilter,
  query: string | null | undefined,
): T[] {
  const q = String(query ?? '')
    .trim()
    .toLowerCase();
  const phoneLike = q !== '' && PHONE_QUERY_RE.test(q);
  const digits = phoneLike ? q.replace(/\D/g, '') : '';
  const expanded = phoneLike ? phoneQueryDigits(q) : '';
  return list.filter((c) => {
    if (filter === 'unread' && c.unread <= 0) return false;
    if (!q) return true;
    if (conversationName(c).toLowerCase().includes(q) || c.phone.includes(q)) return true;
    if (!phoneLike || digits === '') return false;
    const stored = c.phone.replace(/\D/g, '');
    return stored.includes(digits) || stored.includes(expanded);
  });
}

/** Conversations newest first by `lastMessageAt` (nulls last), then by name. */
export function sortConversations<T extends WaConversationLike>(list: readonly T[]): T[] {
  return [...list].sort((a, b) => {
    const ta = a.lastMessageAt ? Date.parse(a.lastMessageAt) : Number.NEGATIVE_INFINITY;
    const tb = b.lastMessageAt ? Date.parse(b.lastMessageAt) : Number.NEGATIVE_INFINITY;
    if (ta !== tb) return tb > ta ? 1 : -1;
    return conversationName(a).localeCompare(conversationName(b), 'sq');
  });
}

/** Prototype `waLast(c)`: the last message or an empty placeholder. */
export function lastMessageOf<T extends Pick<WaMessageLike, 'sentAt'>>(messages: readonly T[]): T | null {
  if (messages.length === 0) return null;
  return (
    [...messages].sort((a, b) => Date.parse(a.sentAt) - Date.parse(b.sentAt))[messages.length - 1] ?? null
  );
}

/**
 * Prototype time labels: `HH:mm` for a message sent today (Europe/Belgrade), `Dje` for yesterday, otherwise
 * the date `dd/MM/yyyy`. Empty for a missing/invalid instant.
 */
export function waTimeLabel(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = dateOf(d);
  const today = dateOf(now);
  if (day === today) return timeOf(d);
  if (day === addDays(today, -1)) return WA_TEXT.yesterday;
  return fmtDate(day);
}

/** Bubble footer: `{time}` for inbound, `{time} · Stafi` for staff replies (prototype renderWaChat). */
export function messageFooter(
  m: Pick<WaMessageLike, 'direction' | 'sentAt'>,
  now: Date = new Date(),
): string {
  return waTimeLabel(m.sentAt, now) + (m.direction === 'out' ? WA_TEXT.staffSuffix : '');
}

/** True while free-form text is allowed (strictly less than 24 h since the customer's last message). */
export function waWindowOpen(
  lastInboundAt: string | Date | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!lastInboundAt) return false;
  const t = lastInboundAt instanceof Date ? lastInboundAt.getTime() : Date.parse(lastInboundAt);
  if (Number.isNaN(t)) return false;
  const age = now.getTime() - t;
  return age >= 0 && age < WA_WINDOW_MS;
}

/** Delivery ticks of an outbound message: ✓ sent, ✓✓ delivered/read, ! failed, nothing before sending. */
export function messageStatusTicks(status: string | null | undefined): string {
  switch (status) {
    case 'sent':
      return '✓';
    case 'delivered':
    case 'read':
      return '✓✓';
    case 'failed':
      return '!';
    default:
      return '';
  }
}

/** Phone as customers read it: `+38349222333` → `+383 49 222 333`; anything else unchanged. */
export function displayWaPhone(phone: string): string {
  const m = /^\+383(\d{2})(\d{3})(\d{3,4})$/.exec(phone);
  return m ? `+383 ${m[1] ?? ''} ${m[2] ?? ''} ${m[3] ?? ''}` : phone;
}
